import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
  getDoc,
  setDoc,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Plant, Customer, Sale, SaleItem, Payment, Worker, WorkerStats, InventoryItem, InventoryStatus, InventoryDispenseRecord } from '../types';

// Plant Services
export const plantService = {
  // Get all plants
  async getAllPlants(): Promise<Plant[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'plants'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Plant[];
    } catch (error) {
      console.error('Error fetching plants:', error);
      throw error;
    }
  },

  // Add new plant
  async addPlant(plantData: Omit<Plant, 'id' | 'createdAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'plants'), {
        ...plantData,
        createdAt: serverTimestamp(),
        isActive: true
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding plant:', error);
      throw error;
    }
  },

  // Update plant
  async updatePlant(id: string, plantData: Partial<Plant>): Promise<void> {
    try {
      const plantRef = doc(db, 'plants', id);
      await updateDoc(plantRef, {
        ...plantData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating plant:', error);
      throw error;
    }
  },

  // Delete plant
  async deletePlant(id: string): Promise<void> {
    try {
      const plantRef = doc(db, 'plants', id);
      await deleteDoc(plantRef);
    } catch (error) {
      console.error('Error deleting plant:', error);
      throw error;
    }
  }
};

// Customer Services
export const customerService = {
  // Get all customers
  async getAllCustomers(): Promise<Customer[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'customers'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Customer[];
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  },

  // Add new customer
  async addCustomer(customerData: Omit<Customer, 'id' | 'registrationDate' | 'totalOrders' | 'totalSpent'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'customers'), {
        ...customerData,
        registrationDate: serverTimestamp(),
        totalOrders: 0,
        totalSpent: 0
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding customer:', error);
      throw error;
    }
  },

  // Update customer
  async updateCustomer(id: string, customerData: Partial<Customer>): Promise<void> {
    try {
      const customerRef = doc(db, 'customers', id);
      
      // Handle null values by converting them to deleteField()
      const processedData: any = {};
      Object.keys(customerData).forEach(key => {
        const value = (customerData as any)[key];
        if (value === null) {
          processedData[key] = deleteField();
        } else {
          processedData[key] = value;
        }
      });
      
      await updateDoc(customerRef, processedData);
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  },

  // Delete customer
  async deleteCustomer(id: string): Promise<void> {
    try {
      const customerRef = doc(db, 'customers', id);
      await deleteDoc(customerRef);
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  },

  // Get customer by ID
  async getCustomerById(id: string): Promise<Customer | null> {
    try {
      const customerRef = doc(db, 'customers', id);
      const customerDoc = await getDoc(customerRef);
      
      if (customerDoc.exists()) {
        return {
          id: customerDoc.id,
          ...customerDoc.data()
        } as Customer;
      }
      return null;
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      throw error;
    }
  },

  // Get customers with calculated stats from orders
  async getCustomersWithStats(): Promise<Customer[]> {
    try {
      // Get all customers
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const customers = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Customer[];

      // Get all orders using the service function
      const orders = await orderService.getAllOrders();

      // Calculate stats for each customer
      const customersWithStats = customers.map(customer => {
        const customerOrders = orders.filter(order => order.customerId === customer.id);
        const totalOrders = customerOrders.length;
        const totalSpent = customerOrders.reduce((sum, order) => sum + order.totalAmount, 0);

        return {
          ...customer,
          totalOrders,
          totalSpent
        };
      });

      return customersWithStats;
    } catch (error) {
      console.error('Error fetching customers with stats:', error);
      throw error;
    }
  }
};

// Helper functions for multi-plant order support

/**
 * Check if an order uses the legacy single-plant format
 */
export const isLegacyOrder = (order: Sale): boolean => {
  return !order.items || order.items.length === 0;
};

/**
 * Get order items, converting legacy single-plant orders to items array format
 */
export const getOrderItems = (order: Sale): SaleItem[] => {
  // If order has items array, return it
  if (order.items && order.items.length > 0) {
    return order.items;
  }

  // Convert legacy single-plant order to items array format
  if (order.plantId) {
    const legacyItem: SaleItem = {
      id: `legacy-${order.id}`,
      plantId: order.plantId,
      plantName: order.plantName || '',
      plantBrand: order.plantBrand,
      plantVariety: order.plantVariety,
      pricePerPlant: (order as any).pricePerPlant || 0,
      quantity: order.quantity || 0,
      totalPrice: order.totalAmount,
      seedsPerTray: order.seedsPerTray || 126,
      traysRequired: order.totalTrays,
    };
    return [legacyItem];
  }

  return [];
};

// Helper function to generate unique 6-digit order ID
const generateOrderId = async (): Promise<string> => {
  try {
    const counterRef = doc(db, 'meta', 'orderCounter');
    
    return await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let nextNumber = 1;
      if (counterDoc.exists()) {
        nextNumber = (counterDoc.data().lastOrderNumber || 0) + 1;
      }
      
      // Update the counter
      transaction.set(counterRef, { lastOrderNumber: nextNumber }, { merge: true });
      
      // Format to 6 digits with leading zeros
      return nextNumber.toString().padStart(6, '0');
    });
  } catch (error) {
    console.error('Error generating order ID:', error);
    throw error;
  }
};

// Order/Sale Services
export const orderService = {
  // Get all orders
  async getAllOrders(): Promise<Sale[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'sales'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Sale[];
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  // Add new order
  // Supports both legacy single-plant orders and new multi-plant orders with items array
  // New flow: create with SeedingPending, without seeding/expectedDelivery dates
  async addOrder(orderData: Omit<Sale, 'id' | 'orderId' | 'orderDate' | 'deliveryDate' | 'expectedDeliveryDate' | 'seedingDate' | 'createdAt' | 'updatedAt' | 'status'>): Promise<{ docId: string; orderId: string }> {
    try {
      // Generate unique 6-digit order ID
      const orderId = await generateOrderId();

      // If items array is provided, calculate totals from items
      let finalOrderData = { ...orderData };
      if (orderData.items && orderData.items.length > 0) {
        const totalAmount = orderData.items.reduce((sum, item) => sum + item.totalPrice, 0);
        const totalTrays = orderData.items.reduce((sum, item) => sum + item.traysRequired, 0);
        finalOrderData = {
          ...orderData,
          totalAmount,
          totalTrays,
          remainingAmount: totalAmount - (orderData.amountPaid || 0),
        };
      }

      const docRef = await addDoc(collection(db, 'sales'), {
        ...finalOrderData,
        orderId,
        orderDate: serverTimestamp(),
        seedingDate: null,
        expectedDeliveryDate: null,
        status: { id: '0', name: 'SeedingPending', description: 'Awaiting seeding date', order: 0 },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update customer stats
      await this.updateCustomerStats(orderData.customerId);

      return { docId: docRef.id, orderId };
    } catch (error) {
      console.error('Error adding order:', error);
      throw error;
    }
  },

  // Get orders pending seeding
  async getSeedingPendingOrders(): Promise<Sale[]> {
    try {
      // Avoid composite index requirement by sorting client-side
      const q = query(
        collection(db, 'sales'),
        where('status.name', '==', 'SeedingPending')
      );
      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Sale[];
      // Sort by orderDate descending on client
      return results.sort((a, b) => {
        const aDate = (a.orderDate as any)?.toDate ? (a.orderDate as any).toDate() : new Date(a.orderDate as any);
        const bDate = (b.orderDate as any)?.toDate ? (b.orderDate as any).toDate() : new Date(b.orderDate as any);
        return bDate.getTime() - aDate.getTime();
      });
    } catch (error) {
      console.error('Error fetching seeding pending orders:', error);
      throw error;
    }
  },

  // Set seeding date and compute expected delivery
  async setSeedingDate(orderId: string, seedingDate: Date, plantGrowthDays: number): Promise<void> {
    try {
      const orderRef = doc(db, 'sales', orderId);
      // Validate current status is SeedingPending
      const snapshot = await getDoc(orderRef);
      if (!snapshot.exists()) {
        throw new Error('Order not found');
      }
      const current = snapshot.data() as Sale;
      if (current.status?.name !== 'SeedingPending') {
        throw new Error('Seeding date can only be set when status is SeedingPending');
      }
      const seedingTs = Timestamp.fromDate(seedingDate);
      const expected = new Date(seedingDate);
      expected.setDate(expected.getDate() + plantGrowthDays);

      await updateDoc(orderRef, {
        seedingDate: seedingTs,
        expectedDeliveryDate: Timestamp.fromDate(expected),
        status: { id: '2', name: 'In Progress', description: 'Order in production', order: 2 },
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error setting seeding date:', error);
      throw error;
    }
  },

  // Update customer stats based on orders
  async updateCustomerStats(customerId: string): Promise<void> {
    try {
      // Use direct query instead of fetching all orders
      const q = query(
        collection(db, 'sales'),
        where('customerId', '==', customerId)
      );
      const querySnapshot = await getDocs(q);

      let totalOrders = 0;
      let totalSpent = 0;

      querySnapshot.forEach(doc => {
        const orderData = doc.data() as Sale;
        totalOrders++;
        totalSpent += orderData.totalAmount || 0;
      });

      // Update customer document
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        totalOrders,
        totalSpent
      });
    } catch (error) {
      console.error('Error updating customer stats:', error);
      throw error;
    }
  },

  // Update order
  async updateOrder(id: string, orderData: Partial<Sale>): Promise<void> {
    try {
      const orderRef = doc(db, 'sales', id);
      await updateDoc(orderRef, {
        ...orderData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  },

  // Update order status
  async updateOrderStatus(id: string, status: { id: string; name: string; description: string; order: number }): Promise<void> {
    try {
      const orderRef = doc(db, 'sales', id);
      await updateDoc(orderRef, {
        status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  },

  // Get orders by customer
  async getOrdersByCustomer(customerId: string): Promise<Sale[]> {
    try {
      const q = query(
        collection(db, 'sales'),
        where('customerId', '==', customerId),
        orderBy('orderDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Sale[];
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      throw error;
    }
  },

  // Get recent orders
  async getRecentOrders(limitCount: number = 5): Promise<Sale[]> {
    try {
      const q = query(
        collection(db, 'sales'),
        orderBy('orderDate', 'desc'),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Sale[];
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      throw error;
    }
  },

  // Search orders by order ID
  async searchOrdersByOrderId(searchTerm: string): Promise<Sale[]> {
    try {
      const allOrders = await this.getAllOrders();
      const normalizedSearch = searchTerm.toLowerCase();
      
      return allOrders.filter(order => {
        if (order.orderId) {
          // Match full or partial order ID
          return order.orderId.includes(normalizedSearch);
        }
        return false;
      });
    } catch (error) {
      console.error('Error searching orders by order ID:', error);
      throw error;
    }
  },

  // Get orders grouped by plant type (for seeding planning)
  // Handles both legacy single-plant orders and multi-plant orders
  async getOrdersGroupedByPlant(): Promise<{ [plantId: string]: { plant: any; orders: Sale[]; totalTrays: number } }> {
    try {
      const pendingOrders = await this.getSeedingPendingOrders();
      const grouped: { [plantId: string]: { plant: any; orders: Sale[]; totalTrays: number } } = {};

      pendingOrders.forEach(order => {
        // Get items (handles both legacy and new format)
        const items = getOrderItems(order);

        items.forEach(item => {
          if (item.plantId) {
            if (!grouped[item.plantId]) {
              grouped[item.plantId] = {
                plant: { id: item.plantId, name: item.plantName, brand: item.plantBrand, variety: item.plantVariety },
                orders: [],
                totalTrays: 0
              };
            }
            // Only add the order once per plant (avoid duplicates)
            if (!grouped[item.plantId].orders.some(o => o.id === order.id)) {
              grouped[item.plantId].orders.push(order);
            }
            grouped[item.plantId].totalTrays += item.traysRequired;
          }
        });
      });

      return grouped;
    } catch (error) {
      console.error('Error grouping orders by plant:', error);
      throw error;
    }
  },

  // Set seeding date for multiple orders (bulk operation)
  async setBulkSeedingDate(orderIds: string[], seedingDate: Date, plantGrowthDays: number): Promise<void> {
    try {
      const batch = [];
      const seedingTs = Timestamp.fromDate(seedingDate);
      const expected = new Date(seedingDate);
      expected.setDate(expected.getDate() + plantGrowthDays);

      for (const orderId of orderIds) {
        const orderRef = doc(db, 'sales', orderId);
        batch.push(
          updateDoc(orderRef, {
            seedingDate: seedingTs,
            expectedDeliveryDate: Timestamp.fromDate(expected),
            status: { id: '2', name: 'In Progress', description: 'Order in production', order: 2 },
            updatedAt: serverTimestamp()
          })
        );
      }
      
      await Promise.all(batch);
    } catch (error) {
      console.error('Error setting bulk seeding date:', error);
      throw error;
    }
  }
};

// Payment Services
export const paymentService = {
  // Get all payments
  async getAllPayments(): Promise<Payment[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'payments'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Payment[];
    } catch (error) {
      console.error('Error fetching payments:', error);
      throw error;
    }
  },

  // Add new payment
  async addPayment(paymentData: Omit<Payment, 'id' | 'paymentDate'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'payments'), {
        ...paymentData,
        paymentDate: serverTimestamp()
      });

      // Update the order's remaining amount and status
      const orderRef = doc(db, 'sales', paymentData.saleId);
      const orderDoc = await getDoc(orderRef);
      
      if (orderDoc.exists()) {
        const orderData = orderDoc.data() as Sale;
        const newRemainingAmount = orderData.remainingAmount - paymentData.amount;
        const newAmountPaid = orderData.amountPaid + paymentData.amount;
        
        // Check if payment is complete
        const isPaymentComplete = newRemainingAmount <= 0;
        
        const updateData: any = {
          remainingAmount: newRemainingAmount,
          amountPaid: newAmountPaid,
          updatedAt: serverTimestamp()
        };

        // Update status to 'Complete' if payment is complete
        if (isPaymentComplete) {
          updateData.status = { id: '5', name: 'Complete', description: 'Payment completed', order: 5 };
        }
        
        await updateDoc(orderRef, updateData);
      }

      return docRef.id;
    } catch (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
  },

  // Get payments by order
  async getPaymentsByOrder(saleId: string): Promise<Payment[]> {
    try {
      const q = query(
        collection(db, 'payments'),
        where('saleId', '==', saleId)
      );
      const querySnapshot = await getDocs(q);
      const payments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Payment[];
      
      // Sort payments by date in descending order on the client side
      return payments.sort((a, b) => {
        const dateA = a.paymentDate?.toDate ? a.paymentDate.toDate() : new Date(a.paymentDate as any);
        const dateB = b.paymentDate?.toDate ? b.paymentDate.toDate() : new Date(b.paymentDate as any);
        return dateB.getTime() - dateA.getTime();
      });
    } catch (error) {
      console.error('Error fetching order payments:', error);
      throw error;
    }
  }
};

// Dashboard Services
export const dashboardService = {
  // Get dashboard stats
  async getDashboardStats() {
    try {
      const [orders, customers, plants] = await Promise.all([
        getDocs(collection(db, 'sales')),
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'plants'))
      ]);

      const totalOrders = orders.size;
      const totalCustomers = customers.size;
      const totalPlants = plants.size;

      let totalRevenue = 0;
      let pendingDeliveries = 0;
      let outstandingAmount = 0;
      let pendingSeeding = 0;

      orders.forEach(doc => {
        const orderData = doc.data() as Sale;
        totalRevenue += orderData.totalAmount;
        outstandingAmount += orderData.remainingAmount;
        
        // Pending seeding
        if (orderData.status?.name === 'SeedingPending') {
          pendingSeeding++;
        }
        
        // Check if delivery is pending (expected delivery date is in the future)
        const expectedDate: any = (orderData as any).expectedDeliveryDate || (orderData as any).deliveryDate;
        if (expectedDate && expectedDate.toDate && expectedDate.toDate() > new Date()) {
          pendingDeliveries++;
        }
      });

      return {
        totalOrders,
        totalRevenue,
        pendingDeliveries,
        totalCustomers,
        totalPlants,
        outstandingAmount,
        pendingSeeding
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }
};

// Report Services
export const reportService = {
  // Get orders within date range
  async getOrdersByDateRange(startDate: Date, endDate: Date): Promise<Sale[]> {
    try {
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      
      const q = query(
        collection(db, 'sales'),
        where('orderDate', '>=', startTimestamp),
        where('orderDate', '<=', endTimestamp),
        orderBy('orderDate', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Sale[];
    } catch (error) {
      console.error('Error fetching orders by date range:', error);
      throw error;
    }
  },

  // Get payments within date range
  async getPaymentsByDateRange(startDate: Date, endDate: Date): Promise<Payment[]> {
    try {
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      
      const q = query(
        collection(db, 'payments'),
        where('paymentDate', '>=', startTimestamp),
        where('paymentDate', '<=', endTimestamp),
        orderBy('paymentDate', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Payment[];
    } catch (error) {
      console.error('Error fetching payments by date range:', error);
      throw error;
    }
  },

  // Get top customers by spending
  async getTopCustomers(limitCount: number = 5): Promise<Customer[]> {
    try {
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const customers = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Customer[];

      // Sort by totalSpent in descending order
      return customers.sort((a, b) => b.totalSpent - a.totalSpent).slice(0, limitCount);
    } catch (error) {
      console.error('Error fetching top customers:', error);
      throw error;
    }
  }
};

// Worker Services
export const workerService = {
  // Get all workers
  async getAllWorkers(): Promise<Worker[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'workers'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as unknown as Worker[];
    } catch (error) {
      console.error('Error fetching workers:', error);
      throw error;
    }
  },

  // Get active workers only
  async getActiveWorkers(): Promise<Worker[]> {
    try {
      const q = query(
        collection(db, 'workers'),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as unknown as Worker[];
    } catch (error) {
      console.error('Error fetching active workers:', error);
      throw error;
    }
  },

  // Add new worker
  async addWorker(workerData: Omit<Worker, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      // Check if phone number already exists
      const existingWorkers = await this.getAllWorkers();
      const phoneExists = existingWorkers.some(worker => 
        worker.phoneNumber === workerData.phoneNumber && worker.isActive
      );
      
      if (phoneExists) {
        throw new Error('Phone number already exists for an active worker');
      }

      const docRef = await addDoc(collection(db, 'workers'), {
        ...workerData,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding worker:', error);
      throw error;
    }
  },

  // Update worker
  async updateWorker(id: string, workerData: Partial<Worker>): Promise<void> {
    try {
      const workerRef = doc(db, 'workers', id);
      await updateDoc(workerRef, {
        ...workerData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating worker:', error);
      throw error;
    }
  },

  // Soft delete worker (mark as inactive)
  async deactivateWorker(id: string): Promise<void> {
    try {
      const workerRef = doc(db, 'workers', id);
      await updateDoc(workerRef, {
        isActive: false,
        availabilityStatus: 'inactive',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deactivating worker:', error);
      throw error;
    }
  },

  // Permanently delete worker
  async deleteWorker(id: string): Promise<void> {
    try {
      const workerRef = doc(db, 'workers', id);
      await deleteDoc(workerRef);
    } catch (error) {
      console.error('Error deleting worker:', error);
      throw error;
    }
  },

  // Get worker by ID
  async getWorkerById(id: string): Promise<Worker | null> {
    try {
      const workerRef = doc(db, 'workers', id);
      const workerDoc = await getDoc(workerRef);
      
      if (workerDoc.exists()) {
        return {
          id: workerDoc.id,
          ...workerDoc.data()
        } as unknown as Worker;
      }
      return null;
    } catch (error) {
      console.error('Error fetching worker by ID:', error);
      throw error;
    }
  },

  // Get worker statistics
  async getWorkerStats(): Promise<WorkerStats> {
    try {
      const workers = await this.getAllWorkers();
      const activeWorkers = workers.filter(worker => worker.isActive);
      
      return {
        totalWorkers: workers.length,
        activeWorkers: activeWorkers.length,
        inactiveWorkers: workers.filter(worker => !worker.isActive).length,
        workersOnLeave: activeWorkers.filter(worker => worker.availabilityStatus === 'on_leave').length
      };
    } catch (error) {
      console.error('Error fetching worker stats:', error);
      throw error;
    }
  },

  // Search workers by name or phone
  async searchWorkers(searchTerm: string): Promise<Worker[]> {
    try {
      const workers = await this.getAllWorkers();
      const lowerSearchTerm = searchTerm.toLowerCase();
      
      return workers.filter(worker => 
        worker.fullName.toLowerCase().includes(lowerSearchTerm) ||
        worker.phoneNumber.includes(searchTerm)
      );
    } catch (error) {
      console.error('Error searching workers:', error);
      throw error;
    }
  },

  // Filter workers by status and gender
  async filterWorkers(filters: { status?: string; gender?: string }): Promise<Worker[]> {
    try {
      const workers = await this.getAllWorkers();

      return workers.filter(worker => {
        const statusMatch = !filters.status || worker.availabilityStatus === filters.status;
        const genderMatch = !filters.gender || worker.gender === filters.gender;
        return statusMatch && genderMatch;
      });
    } catch (error) {
      console.error('Error filtering workers:', error);
      throw error;
    }
  }
};

// Inventory Service - Nursery's own seedlings
export const inventoryService = {
  // Get all inventory items
  async getAllInventoryItems(): Promise<InventoryItem[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'inventory'));
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as InventoryItem[];
      // Sort by seeding date descending
      return items.sort((a, b) => {
        const aDate = (a.seedingDate as any)?.toDate ? (a.seedingDate as any).toDate() : new Date(a.seedingDate as any);
        const bDate = (b.seedingDate as any)?.toDate ? (b.seedingDate as any).toDate() : new Date(b.seedingDate as any);
        return bDate.getTime() - aDate.getTime();
      });
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      throw error;
    }
  },

  // Get inventory item by ID
  async getInventoryItemById(id: string): Promise<InventoryItem | null> {
    try {
      const docRef = doc(db, 'inventory', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as InventoryItem;
      }
      return null;
    } catch (error) {
      console.error('Error fetching inventory item:', error);
      throw error;
    }
  },

  // Add new inventory item
  async addInventoryItem(itemData: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      // Filter out undefined values as Firebase doesn't accept them
      const cleanedData = Object.fromEntries(
        Object.entries(itemData).filter(([, value]) => value !== undefined)
      );
      const docRef = await addDoc(collection(db, 'inventory'), {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding inventory item:', error);
      throw error;
    }
  },

  // Update inventory item
  async updateInventoryItem(id: string, itemData: Partial<InventoryItem>): Promise<void> {
    try {
      // Filter out undefined values as Firebase doesn't accept them
      const cleanedData = Object.fromEntries(
        Object.entries(itemData).filter(([, value]) => value !== undefined)
      );
      const docRef = doc(db, 'inventory', id);
      await updateDoc(docRef, {
        ...cleanedData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw error;
    }
  },

  // Delete inventory item
  async deleteInventoryItem(id: string): Promise<void> {
    try {
      const docRef = doc(db, 'inventory', id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      throw error;
    }
  },

  // Dispense from inventory (farmer takes plants/trays)
  async dispenseFromInventory(
    inventoryItemId: string,
    traysToDispense: number,
    customerName?: string,
    customerPhone?: string,
    notes?: string
  ): Promise<string> {
    try {
      const item = await this.getInventoryItemById(inventoryItemId);
      if (!item) {
        throw new Error('Inventory item not found');
      }

      if (traysToDispense > item.availableTrays) {
        throw new Error(`Cannot dispense ${traysToDispense} trays. Only ${item.availableTrays} available.`);
      }

      const plantsDispensed = traysToDispense * item.seedsPerTray;
      const newAvailableTrays = item.availableTrays - traysToDispense;
      const newAvailablePlants = item.availablePlants - plantsDispensed;

      // Determine new status
      let newStatus: InventoryStatus;
      if (newAvailableTrays === 0) {
        newStatus = { id: '5', name: 'Fully Dispensed', description: 'All trays dispensed' };
      } else if (newAvailableTrays < item.totalTrays) {
        newStatus = { id: '4', name: 'Partially Dispensed', description: 'Some trays dispensed' };
      } else {
        newStatus = item.status;
      }

      // Update inventory item
      await this.updateInventoryItem(inventoryItemId, {
        availableTrays: newAvailableTrays,
        availablePlants: newAvailablePlants,
        status: newStatus
      });

      // Create dispense record - filter out undefined values
      const dispenseRecord: Record<string, unknown> = {
        inventoryItemId,
        traysDispensed: traysToDispense,
        plantsDispensed,
        dispenseDate: Timestamp.now()
      };
      if (customerName) dispenseRecord.customerName = customerName;
      if (customerPhone) dispenseRecord.customerPhone = customerPhone;
      if (notes) dispenseRecord.notes = notes;

      const recordRef = await addDoc(collection(db, 'inventoryDispenseRecords'), {
        ...dispenseRecord,
        createdAt: serverTimestamp()
      });

      return recordRef.id;
    } catch (error) {
      console.error('Error dispensing from inventory:', error);
      throw error;
    }
  },

  // Get dispense records for an inventory item
  async getDispenseRecords(inventoryItemId: string): Promise<InventoryDispenseRecord[]> {
    try {
      const q = query(
        collection(db, 'inventoryDispenseRecords'),
        where('inventoryItemId', '==', inventoryItemId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as InventoryDispenseRecord[];
    } catch (error) {
      console.error('Error fetching dispense records:', error);
      throw error;
    }
  },

  // Get all dispense records
  async getAllDispenseRecords(): Promise<InventoryDispenseRecord[]> {
    try {
      const q = query(
        collection(db, 'inventoryDispenseRecords'),
        orderBy('dispenseDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as InventoryDispenseRecord[];
    } catch (error) {
      console.error('Error fetching all dispense records:', error);
      throw error;
    }
  },

  // Update inventory status based on expected ready date
  async updateInventoryStatus(id: string): Promise<void> {
    try {
      const item = await this.getInventoryItemById(id);
      if (!item) return;

      const today = new Date();
      const expectedDate = (item.expectedReadyDate as any)?.toDate
        ? (item.expectedReadyDate as any).toDate()
        : new Date(item.expectedReadyDate as any);

      let newStatus: InventoryStatus;
      if (item.availableTrays === 0) {
        newStatus = { id: '5', name: 'Fully Dispensed', description: 'All trays dispensed' };
      } else if (item.availableTrays < item.totalTrays) {
        newStatus = { id: '4', name: 'Partially Dispensed', description: 'Some trays dispensed' };
      } else if (today >= expectedDate) {
        newStatus = { id: '3', name: 'Ready', description: 'Ready for dispensing' };
      } else {
        // Calculate days remaining
        const daysRemaining = Math.ceil((expectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        if (daysRemaining <= 7) {
          newStatus = { id: '2', name: 'Growing', description: 'Plants are growing' };
        } else {
          newStatus = { id: '1', name: 'Seeding', description: 'Recently seeded' };
        }
      }

      if (newStatus.name !== item.status.name) {
        await this.updateInventoryItem(id, { status: newStatus });
      }
    } catch (error) {
      console.error('Error updating inventory status:', error);
      throw error;
    }
  }
};

// Contact/Settings Service
export interface ContactInfoData {
  primaryPhone: string;
  secondaryPhone: string;
  email: string;
  address: string;
  businessHours: {
    weekdays: string;
    weekends: string;
    note: string;
  };
  mapsLink: string;
  updatedAt?: any;
}

export const contactService = {
  // Get contact info from Firestore
  async getContactInfo(): Promise<ContactInfoData | null> {
    try {
      const docRef = doc(db, 'settings', 'contactInfo');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as ContactInfoData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching contact info:', error);
      throw error;
    }
  },

  // Save contact info to Firestore
  async saveContactInfo(contactData: ContactInfoData): Promise<void> {
    try {
      const docRef = doc(db, 'settings', 'contactInfo');
      await setDoc(docRef, {
        ...contactData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving contact info:', error);
      throw error;
    }
  }
}; 